/**
 * feat-196 (B3) — fake-mode data for the TENANT surface only.
 *
 * ## Why this file exists at all
 *
 * B3 was written as "the fake-mode seed needs one live and one non-live tenant."
 * There was no such seed to extend. `DummyDataService` is reachable only through
 * `RestaurantBackend.apiCall` and holds no tenant data whatsoever, and `utils/api`
 * — which is what `TenantDashboard` actually calls — had no fake branch at all. So
 * the "seed" had to bring its own plumbing.
 *
 * ## Why it stops here
 *
 * product-lead scoped B3 down (option iii) to the TENANT surface only, so that
 * AC12 (the Admin-only control renders with its note text) and AC13 (flipping it
 * persists and survives a refresh) are demonstrable with no backend. The
 * Interaction Evaluations pages are deliberately NOT covered: they go through
 * `scopedReadsService`, which has no fake branch either, and wiring that up is a
 * materially larger change than D9's tight slice allows. AC10/AC11 are verified by
 * Jest plus the dev backend once the backend slice lands.
 *
 * Note `scopedReadsService`'s own header comment claims "every consumer
 * short-circuits to dummy data BEFORE calling these" — that is FALSE today for the
 * three eval components and is tracked as FU-2. Do not read it as evidence that
 * fake mode covers those pages.
 *
 * ## The store is mutable on purpose
 *
 * AC13 is "flipping it in the UI persists and is reflected on the eval pages after
 * refresh." Within a fake-mode session the PUT has to actually change what the
 * next GET returns, or the control would appear to revert and would demonstrate
 * the exact failure mode B2 is built to make visible. This is module state, so it
 * resets on a full page reload — which is the honest limit of a backend-less
 * demo, and is called out in `README`-less form right here so nobody reports it.
 */

export interface FakeTenantRow {
  tenant_id: string;
  tenant_name: string;
  description: string;
  created: string;
  default_language: string;
  /** feat-196 — CLIENT STATUS. The whole point of this fixture. */
  is_live: boolean;
  /** feat-303 — tenant drip release (default off, rate 1). */
  drip_enabled: boolean;
  drip_lessons_per_day: number;
  /** feat-305 — the manager assistant (default off). */
  assistant_enabled: boolean;
  /** feat-354 — the daily lesson auto-copy (default off). */
  learn_auto_copy_enabled: boolean;
}

/**
 * The seed: at least one LIVE and one NON-LIVE tenant, which is what makes the
 * toggle and the admin control testable at all.
 *
 * The names are deliberately chosen to mirror the real-world shape that motivated
 * feat-196 — a genuine client alongside a demo tenant created for a sales
 * walkthrough, which is exactly the population that was polluting Interaction
 * Evaluations. The third row exists so "non-live" isn't a single special case.
 */
const SEED: FakeTenantRow[] = [
  {
    tenant_id: '11111111-1111-1111-1111-111111111111',
    tenant_name: 'Ladle & Leaf',
    description: 'Live client — recording real customer interactions.',
    created: '2026-01-15T09:00:00',
    default_language: 'en',
    is_live: true,
    // feat-303 — Ladle & Leaf is the tenant that asked for drip: on, 1/day.
    drip_enabled: true,
    drip_lessons_per_day: 1,
    // feat-305 — one tenant on so both toggle states are visible in start:fake.
    assistant_enabled: true,
    // feat-354 — one tenant on so both toggle states are visible in start:fake.
    learn_auto_copy_enabled: true,
  },
  {
    tenant_id: '22222222-2222-2222-2222-222222222222',
    tenant_name: 'Ladle & Leaf Demo',
    description: 'Demo tenant used for sales walkthroughs — not a live client.',
    created: '2026-03-02T09:00:00',
    default_language: 'en',
    is_live: false,
    drip_enabled: false,
    drip_lessons_per_day: 1,
    assistant_enabled: false,
    learn_auto_copy_enabled: false,
  },
  {
    tenant_id: '33333333-3333-3333-3333-333333333333',
    tenant_name: 'Harbour Street Kitchen',
    description: 'Onboarding — not yet recording real interactions.',
    created: '2026-06-20T09:00:00',
    default_language: 'en',
    is_live: false,
    drip_enabled: false,
    drip_lessons_per_day: 1,
    assistant_enabled: false,
    learn_auto_copy_enabled: false,
  },
];

/** Session-scoped mutable copy. Reset on page reload (see the header note). */
let tenants: FakeTenantRow[] = SEED.map((t) => ({ ...t }));

/** GET /api/tenant — the flat tenant list TenantDashboard renders. */
export function fakeListTenants(): FakeTenantRow[] {
  return tenants.map((t) => ({ ...t }));
}

/**
 * PUT /api/tenant/{id} — applies only the fields present in the body, mirroring
 * the backend's dynamic-field update. `is_live` is applied only when it is
 * actually present, which is the same "was it provided" sentinel the backend uses
 * (`Optional[bool] = None`) — so a body that omits it leaves the flag alone,
 * exactly as the real endpoint does.
 */
export function fakeUpdateTenant(
  tenantId: string,
  body: {
    tenant_name?: string;
    description?: string;
    default_language?: string;
    is_live?: boolean;
    drip_enabled?: boolean;
    drip_lessons_per_day?: number;
    assistant_enabled?: boolean;
    learn_auto_copy_enabled?: boolean;
  },
): FakeTenantRow | null {
  const row = tenants.find((t) => t.tenant_id === tenantId);
  if (!row) return null;
  if (body.tenant_name !== undefined) row.tenant_name = body.tenant_name;
  if (body.description !== undefined) row.description = body.description;
  if (body.default_language !== undefined) row.default_language = body.default_language;
  if (body.is_live !== undefined && body.is_live !== null) row.is_live = !!body.is_live;
  // feat-303 — same `is not None` sentinel as the backend's tenant drip write.
  if (body.drip_enabled !== undefined && body.drip_enabled !== null) row.drip_enabled = !!body.drip_enabled;
  if (body.drip_lessons_per_day !== undefined && body.drip_lessons_per_day !== null) {
    row.drip_lessons_per_day = body.drip_lessons_per_day;
  }
  // feat-305 — same `is not None` sentinel as the backend.
  if (body.assistant_enabled !== undefined && body.assistant_enabled !== null) {
    row.assistant_enabled = !!body.assistant_enabled;
  }
  // feat-354 — same `is not None` sentinel as the backend (CONTRACT C1b).
  if (body.learn_auto_copy_enabled !== undefined && body.learn_auto_copy_enabled !== null) {
    row.learn_auto_copy_enabled = !!body.learn_auto_copy_enabled;
  }
  return { ...row };
}

/** Restore the seed. Test-only — not called by the app.
 *  feat-260: also restores the translation-language store, so a caller that
 *  resets "the fake tenants" is not left holding half a reset. */
export function resetFakeTenants(): void {
  tenants = SEED.map((t) => ({ ...t }));
  resetFakeTenantTranslationLanguages();
}

// ============================================================
// feat-260 — per-tenant translation languages (ADR 0001 §7.3)
// ============================================================

/**
 * ## Why this half of the fixture exists
 *
 * Without it `npm run start:fake` renders `TenantTranslationLanguagesCard`
 * permanently in the **load-failed** state (`design/states.md` §2b): `fetchApi`
 * would fall through this file's `null` and issue a real network request that
 * nothing is listening to. The card is correct to show an error there — that is
 * exactly the deploy-skew state spec AT20 is about — but a fake mode that can
 * only ever demonstrate the error state cannot demonstrate the feature.
 *
 * ## The universe is the server's, and in fake mode this file IS the server
 *
 * ADR 0001 §7.4 and `design/copy.md` §4 forbid the CARD from hard-coding the
 * checkbox list; the list must arrive in the GET response. That constraint is on
 * the client. Here we are standing in for the backend, whose `supported_languages`
 * is `sorted(SUPPORTED_LOCALES - {'en'})` — `["es"]` today — so naming it is not
 * the violation, it is the fixture's job. Widen this array and the card grows a
 * checkbox with no component change, which is the property worth having and the
 * one this fixture lets someone check by hand.
 */
const FAKE_SUPPORTED_TRANSLATION_LANGUAGES: string[] = ['es'];

/**
 * The seed: **one tenant with Spanish and two with nothing.**
 *
 * Both populations are load-bearing for a hand walkthrough, and neither is the
 * other's edge case:
 *
 * - Ladle & Leaf carries `['es']` because it is the tenant the real seeding
 *   migration leaves with Spanish (spec §Solution shape item 2) — it demonstrates
 *   `states.md` §3, the happy path, and it is what unchecking has to be tried
 *   against to see §6's removal confirmation.
 * - The other two carry `[]`, which is **Grove's state today** and the state the
 *   migration leaves most tenants in. It is `states.md` §2 — a *valid, deliberate
 *   configuration*, not an empty state — and it is the render that AT20 exists to
 *   keep distinguishable from a failed load. Somebody comparing §2 against §2b by
 *   eye needs a tenant that actually sits in §2.
 */
const TRANSLATION_LANGUAGES_SEED: Record<string, string[]> = {
  '11111111-1111-1111-1111-111111111111': ['es'],
  '22222222-2222-2222-2222-222222222222': [],
  '33333333-3333-3333-3333-333333333333': [],
};

function cloneTranslationLanguagesSeed(): Record<string, string[]> {
  const copy: Record<string, string[]> = {};
  Object.keys(TRANSLATION_LANGUAGES_SEED).forEach((id) => {
    copy[id] = [...TRANSLATION_LANGUAGES_SEED[id]];
  });
  return copy;
}

/** Session-scoped mutable copy, as with `tenants` above. */
let translationLanguages: Record<string, string[]> = cloneTranslationLanguagesSeed();

/** The response shape both routes return — ADR 0001 §7.3
 *  `TranslationLanguagesResponse`, identical for GET and PUT. */
export interface FakeTenantTranslationLanguages {
  tenant_id: string;
  translation_languages: string[];
  supported_languages: string[];
}

function translationLanguagesResponse(tenantId: string): FakeTenantTranslationLanguages {
  return {
    tenant_id: tenantId,
    translation_languages: [...(translationLanguages[tenantId] ?? [])],
    supported_languages: [...FAKE_SUPPORTED_TRANSLATION_LANGUAGES],
  };
}

/**
 * GET /api/tenant/{id}/translation-languages.
 *
 * An unknown tenant answers with an **empty list**, not `null`. The real route
 * 404s (§7.3), and mirroring that here is tempting — but `fakeTenantResponse`
 * reads `null` as "I do not serve this path", so returning it would send fake
 * mode out to the network, which is the one thing fake mode must never do. The
 * fixture's tenant ids are its own, so an unknown id is a fixture bug rather than
 * a state worth reproducing faithfully.
 */
export function fakeGetTenantTranslationLanguages(
  tenantId: string,
): FakeTenantTranslationLanguages {
  return translationLanguagesResponse(tenantId);
}

/**
 * PUT /api/tenant/{id}/translation-languages.
 *
 * Mirrors the §7.3 handler's normalization so that what the card reads back is
 * what the real backend would have stored, not what the card sent: lower-cased,
 * `''` and `'en'` dropped (English is never a translation target, spec AT6),
 * deduped, restricted to the supported set, and **sorted**.
 *
 * Sorting and `en`-dropping are not cosmetic here. `states.md` §3 requires the
 * card to re-render from the PUT response rather than from the checkbox state it
 * submitted, *because* the server normalizes — a fixture that echoed the request
 * back verbatim would make that requirement untestable by hand and would let a
 * card that renders local state look correct in fake mode.
 *
 * An empty array is a **real, supported action** ("translate into nothing", spec
 * AT4), not a no-op and not an error.
 */
export function fakeSetTenantTranslationLanguages(
  tenantId: string,
  languages: string[],
): FakeTenantTranslationLanguages {
  const normalized = Array.from(
    new Set(
      (languages ?? [])
        .map((l) => String(l ?? '').trim().toLowerCase())
        .filter((l) => l !== '' && l !== 'en')
        .filter((l) => FAKE_SUPPORTED_TRANSLATION_LANGUAGES.includes(l)),
    ),
  ).sort();
  translationLanguages[tenantId] = normalized;
  return translationLanguagesResponse(tenantId);
}

/** Restore the translation-language seed. Test-only, and also called by
 *  `resetFakeTenants()` so a test resetting "the fake tenants" gets all of it. */
export function resetFakeTenantTranslationLanguages(): void {
  translationLanguages = cloneTranslationLanguagesSeed();
}

// ============================================================
// feat-284 — GET /api/tenant/{id}/learn-content
// ============================================================

/**
 * ## Why this fixture exists
 *
 * `/quiz-review` had no fake-mode data at all: the request fell through to a real
 * network call that nothing answers, so `npm run start:fake` showed the page's red
 * error box and nothing else. That made `FRONTEND-SLICE.md` §10's prescribed visual
 * check impossible without pointing at a backend — and `start:local` is off limits
 * on this repo (it aims Cognito at the PROD user pool).
 *
 * It is scoped to READS of this one path. Nothing here fakes the curriculum EDITOR's
 * writes; `QuizContentManagement.tsx` in fake mode is exactly as (un)supported as it
 * was before.
 *
 * ## Why the overlay is shaped the way it is
 *
 * The fixture deliberately seeds **one untranslated field** (`option_c`, plus the
 * unit subtitle) and **one stale field** (the 💡 explanation, and one rehearsal
 * criterion) so that both markers and neither-marker are all visible on one screen
 * without hunting. Do not "complete" the Spanish here — a fully translated fixture
 * makes the feature look finished while rendering none of it.
 *
 * `lang` is the API's SINGLE language code. It is never `en,es`: that is the
 * browser's Both-mode URL value, and `normalize_lang` would read it as `en`.
 */
const FAKE_LEARN_CONTENT_EN = {
  unitTitle: 'Greeting Every Guest',
  unitSubtitle: 'Why the first ten seconds decide the whole visit.',
  drillTitle: 'The First 30 Seconds',
  q1: 'A guest sits down at table 12 while you are carrying three plates to another table. What do you do?',
  q1a: 'Finish the delivery, then greet them — it has only been about two minutes.',
  q1b: 'Make eye contact, say you will be right with them, then finish the delivery.',
  q1c: 'Put the plates down at the nearest table and greet the new guest first.',
  q1d: 'Ask whoever is closest to the host stand to greet them for you.',
  q1explain:
    'A guest who has been acknowledged will wait happily. A guest who has been ignored starts counting.',
  q1praise: 'Exactly — acknowledgement costs you two seconds and buys you two minutes.',
  q2: 'What is the longest a seated guest should wait before anyone speaks to them?',
  q2a: 'Thirty seconds.',
  q2b: 'Two minutes.',
  q2c: 'Until their whole party arrives.',
  q2d: 'Until you finish with your current table.',
  q2explain: 'Thirty seconds is the number on the back wall. It is a limit, not a target.',
  q2praise: 'Right — thirty seconds, every time.',
  // feat-325 — a fill_blank so the Grading mode control and "Try an answer"
  // render under `npm run start:fake`.
  q3: 'We are passionate about ___.',
  q3a: 'Food',
  q3b: 'Money',
  q3c: 'Speed',
  q3d: 'Rules',
  q3explain: 'Everything we do starts with the food.',
  q3praise: 'Yes — food first, always.',
  rehearsalName: 'Greet a table of four at the door',
  rehearsalScenario:
    'A family of four walks in at 6:40pm on a Friday. The host stand is empty and you are the nearest server.',
  goodLabel: 'Warm opening',
  goodDescription: 'Greets within ten seconds of eye contact.',
  goodLabel2: 'Names the wait',
  goodDescription2: 'Tells the guest how many minutes the real wait is.',
  badLabel: 'Points and walks away',
  badDescription: 'Gestures at a table without walking the guest to it.',
  videoTitle: 'Table 12, three plates, one guest',
  readingTitle: 'The thirty-second rule, and why it exists',
  readingBody:
    'Every complaint we have ever logged about "slow service" traces back to the gap between sitting down and being spoken to — not to the kitchen.',
};

const FAKE_LEARN_CONTENT_ES = {
  unitTitle: 'Saludar a Cada Huésped',
  drillTitle: 'Los Primeros 30 Segundos',
  q1: 'Un huésped se sienta en la mesa 12 mientras usted lleva tres platos a otra mesa. ¿Qué hace?',
  q1a: 'Termine la entrega y luego salúdelos: solo han pasado unos dos minutos.',
  q1b: 'Haga contacto visual, dígales que estará con ellos enseguida y luego termine la entrega.',
  q1d: 'Pídale a la persona más cercana al puesto de anfitrión que los salude por usted.',
  q1explain:
    'Un huésped al que han reconocido espera con gusto. Un huésped al que ignoran empieza a contar.',
  q1praise: 'Exacto: reconocerlos le cuesta dos segundos y le compra dos minutos.',
  q2: '¿Cuál es el tiempo máximo que un huésped sentado debería esperar antes de que alguien le hable?',
  q2a: 'Treinta segundos.',
  q2b: 'Dos minutos.',
  q2c: 'Hasta que llegue todo su grupo.',
  q2d: 'Hasta que usted termine con su mesa actual.',
  q2explain:
    'Treinta segundos es el número que está en la pared de atrás. No es una meta, es un límite.',
  q2praise: 'Correcto: treinta segundos, siempre.',
  q3: 'Nos apasiona ___.',
  q3a: 'La comida',
  q3b: 'El dinero',
  q3c: 'La rapidez',
  q3d: 'Las reglas',
  q3explain: 'Todo lo que hacemos empieza con la comida.',
  q3praise: 'Sí: la comida primero, siempre.',
  rehearsalName: 'Salude a una mesa de cuatro en la puerta',
  rehearsalScenario:
    'Una familia de cuatro entra a las 6:40 p. m. un viernes. El puesto de anfitrión está vacío y usted es el mesero más cercano.',
  goodLabel: 'Apertura cálida',
  goodLabel2: 'Menciona la espera',
  goodDescription2: 'Le dice al huésped cuántos minutos es la espera real.',
  badLabel: 'Señala y se va',
  badDescription: 'Señala una mesa sin acompañar al huésped hasta ella.',
  readingTitle: 'La regla de los treinta segundos, y por qué existe',
  readingBody:
    'Cada queja que hemos registrado sobre "servicio lento" se remonta al intervalo entre sentarse y que alguien le hable — no a la cocina.',
};

function t(text: string, stale = false) {
  return { text, status: stale ? 'approved' : 'fresh', stale };
}

/**
 * GET /api/tenant/{id}/learn-content, with the feat-284 `lang` overlay.
 *
 * Without `lang` the response carries NO `translations` key anywhere — byte-for-byte
 * what the editor reads today. With `lang=es` every canonical English field is
 * unchanged and the overlay is added alongside it, which is the additive contract
 * the real endpoint implements.
 */
export function fakeGetLearnContent(tenantId: string, lang?: string): unknown {
  const en = FAKE_LEARN_CONTENT_EN;
  const es = FAKE_LEARN_CONTENT_ES;
  const want = lang === 'es';
  const tr = <T,>(overlay: T) => (want ? { translations: { es: overlay } } : {});
  const content = {
    tenant_id: tenantId,
    lessons: [
      {
        id: 'fake-unit-1',
        title: en.unitTitle,
        subtitle: en.unitSubtitle,
        emoji: '👋',
        mascot_key: null,
        image_s3_key: null,
        sort_order: 1,
        active: true,
        // feat-303 — exercises the "Skip drip" tree pill / strip toggle.
        drip_bypass: true,
        // `subtitle` is deliberately absent from the overlay — untranslated.
        ...tr({ title: t(es.unitTitle) }),
        drills: [
          {
            id: 'fake-drill-1',
            title: en.drillTitle,
            emoji: '⏱️',
            icon_key: null,
            xp_reward: 25,
            question_count: 5,
            sort_order: 1,
            active: true,
            ...tr({ title: t(es.drillTitle) }),
            question_items: [
              {
                id: 'fake-q-1',
                question_type: 'multiple_choice',
                question_text: en.q1,
                option_a: en.q1a,
                option_b: en.q1b,
                option_c: en.q1c,
                option_d: en.q1d,
                correct_option: 'b',
                explanation: en.q1explain,
                correct_explanation: en.q1praise,
                xp_value: null,
                sort_order: 1,
                // `option_c` absent ⇒ untranslated; the explanation is STALE.
                ...tr({
                  question_text: t(es.q1),
                  option_a: t(es.q1a),
                  option_b: t(es.q1b),
                  option_d: t(es.q1d),
                  explanation: t(es.q1explain, true),
                  correct_explanation: t(es.q1praise),
                }),
              },
              {
                id: 'fake-q-2',
                question_type: 'multiple_choice',
                question_text: en.q2,
                option_a: en.q2a,
                option_b: en.q2b,
                option_c: en.q2c,
                option_d: en.q2d,
                correct_option: 'a',
                explanation: en.q2explain,
                correct_explanation: en.q2praise,
                xp_value: null,
                sort_order: 2,
                // Fully translated — the "no marker at all" reference case.
                ...tr({
                  question_text: t(es.q2),
                  option_a: t(es.q2a),
                  option_b: t(es.q2b),
                  option_c: t(es.q2c),
                  option_d: t(es.q2d),
                  explanation: t(es.q2explain),
                  correct_explanation: t(es.q2praise),
                }),
              },
              {
                id: 'fake-q-3',
                question_type: 'fill_blank',
                question_text: en.q3,
                option_a: en.q3a,
                option_b: en.q3b,
                option_c: en.q3c,
                option_d: en.q3d,
                correct_option: 'a',
                explanation: en.q3explain,
                correct_explanation: en.q3praise,
                xp_value: null,
                blank_accepted_variants: 'real food',
                // feat-325: null = Auto.
                blank_grading_mode: null,
                sort_order: 3,
                ...tr({
                  question_text: t(es.q3),
                  option_a: t(es.q3a),
                  option_b: t(es.q3b),
                  option_c: t(es.q3c),
                  option_d: t(es.q3d),
                  explanation: t(es.q3explain),
                  correct_explanation: t(es.q3praise),
                }),
              },
            ],
            rehearsal_items: [
              {
                id: 'fake-r-1',
                drill_id: 'fake-drill-1',
                role: 'server',
                name: en.rehearsalName,
                scenario_text: en.rehearsalScenario,
                scenario_image_s3_key: null,
                prompt_template_version_id: null,
                prompt_template_version_number: null,
                xp_value: 40,
                location_label: null,
                correct_example_response: null,
                redo_xp: 10,
                fail_xp: 0,
                max_recording_seconds: 45,
                sort_order: 3,
                ...tr({ name: t(es.rehearsalName), scenario_text: t(es.rehearsalScenario) }),
                criteria: [
                  {
                    kind: 'good',
                    label: en.goodLabel,
                    description: en.goodDescription,
                    example_phrases: null,
                    position: 1,
                    // description absent ⇒ untranslated, inline, chip only.
                    ...tr({ label: t(es.goodLabel) }),
                  },
                  {
                    kind: 'good',
                    label: en.goodLabel2,
                    description: en.goodDescription2,
                    example_phrases: null,
                    position: 2,
                    ...tr({ label: t(es.goodLabel2), description: t(es.goodDescription2) }),
                  },
                  {
                    kind: 'bad',
                    label: en.badLabel,
                    description: en.badDescription,
                    example_phrases: null,
                    position: 3,
                    ...tr({ label: t(es.badLabel, true), description: t(es.badDescription) }),
                  },
                ],
              },
            ],
            video_items: [
              {
                id: 'fake-v-1',
                drill_id: 'fake-drill-1',
                title: en.videoTitle,
                sort_order: 4,
                captions_status: 'ready',
                duration_seconds: 134,
                published: true,
                // NO overlay, in any mode — video titles are not translated here.
              },
            ],
            reading_items: [
              {
                id: 'fake-rd-1',
                drill_id: 'fake-drill-1',
                title: en.readingTitle,
                body: en.readingBody,
                sort_order: 5,
                image_s3_keys: [],
                image_urls: [],
                ...tr({ title: t(es.readingTitle), body: t(es.readingBody) }),
              },
            ],
          },
        ],
      },
      // feat-354 — two auto-copies (CONTRACT C2) so the "Copy" tree chip and the
      // "Copy of" detail line render under start:fake: one whose original is
      // live (links to fake-unit-1), one whose original was deleted (no link).
      {
        id: 'fake-unit-copy-1',
        title: `${en.unitTitle}: Round Two`,
        subtitle: en.unitSubtitle,
        emoji: '👋',
        mascot_key: null,
        image_s3_key: null,
        sort_order: 2,
        active: true,
        drip_bypass: false,
        copied_from_lesson_id: 'fake-unit-1',
        copied_from_lesson_title: en.unitTitle,
        drills: [],
      },
      {
        id: 'fake-unit-copy-2',
        title: 'Closing Duties: Round Two',
        subtitle: '',
        emoji: '🧹',
        mascot_key: null,
        image_s3_key: null,
        sort_order: 3,
        active: true,
        drip_bypass: false,
        copied_from_lesson_id: 'fake-unit-deleted',
        copied_from_lesson_title: null,
        drills: [],
      },
    ],
  };
  return applyFakeDrillMoves(content);
}

/**
 * feat-361 — fake-mode `POST /api/learn-drills/{id}/move`. The fake learn content is
 * rebuilt on every GET, so moves are kept here (module state, lost on page reload)
 * and replayed onto each GET. Mirrors the backend: the drill goes LAST in the
 * destination (`max(sort_order) + 1`, or 0 when empty); same-lesson is a 400.
 */
const fakeDrillMoves = new Map<string, string>();

type FakeUnit = { id: string; drills: Array<{ id: string; sort_order: number }> };

function applyFakeDrillMoves<T extends { lessons: FakeUnit[] }>(content: T): T {
  fakeDrillMoves.forEach((targetId, drillId) => {
    const source = content.lessons.find((u) => u.drills.some((d) => d.id === drillId));
    const target = content.lessons.find((u) => u.id === targetId);
    if (!source || !target || source === target) return;
    const drill = source.drills.find((d) => d.id === drillId)!;
    source.drills = source.drills.filter((d) => d.id !== drillId);
    const sort = target.drills.length ? Math.max(...target.drills.map((d) => d.sort_order)) + 1 : 0;
    target.drills = [...target.drills, { ...drill, sort_order: sort }];
  });
  return content;
}

export function fakeMoveDrill(
  drillId: string,
  targetLessonId: string,
): { status: string; parent_lesson_id: string; sort_order: number } {
  const content = fakeGetLearnContent('fake') as { lessons: FakeUnit[] };
  const source = content.lessons.find((u) => u.drills.some((d) => d.id === drillId));
  const target = content.lessons.find((u) => u.id === targetLessonId);
  if (!source || !target) throw new Error('Drill or Lesson not found');
  if (source === target) throw new Error('Drill is already in that lesson');
  fakeDrillMoves.set(drillId, targetLessonId);
  const moved = (fakeGetLearnContent('fake') as { lessons: FakeUnit[] }).lessons
    .find((u) => u.id === targetLessonId)!
    .drills.find((d) => d.id === drillId)!;
  return { status: 'ok', parent_lesson_id: targetLessonId, sort_order: moved.sort_order };
}

// ============================================================
// feat-299 — `/quiz-review` inline edit in fake mode
// ============================================================

/**
 * The two feat-270 approval READS `/quiz-review` makes, answered so the pending
 * chips and page note are visible under `npm run start:fake`. Static on purpose:
 * fake mode has no live-row-vs-snapshot to derive pending from, so a save never
 * changes this answer — which is also an honest picture of the rule that the
 * page must not assume "I just saved it" means pending (spec §5).
 *
 * `fake-unit-1` is an approved-then-edited Lesson with `fake-q-2` and `fake-r-1`
 * waiting. Shapes follow `ApprovalLessonDetail` / `ApprovalQueue`.
 */
function fakeApprovalLesson(tenantId: string): Record<string, unknown> {
  const items = [
    ['question_item', 'fake-q-2'],
    ['rehearsal_template', 'fake-r-1'],
  ].map(([entityType, entityId], i) => ({
    entity_ref: { entity_type: entityType, entity_id: entityId, lesson_id: 'fake-unit-1', drill_id: 'fake-drill-1' },
    change_kind: 'updated',
    label: entityId,
    kind_label: null,
    position: i + 1,
    parent_drill_title: null,
    changed_by: null,
    changed_at: null,
    history_id: null,
    restorable: false,
    fields: [],
    is_new_since_review: null,
  }));
  return {
    lesson_id: 'fake-unit-1',
    title: FAKE_LEARN_CONTENT_EN.unitTitle,
    emoji: '👋',
    sort_order: 1,
    state: 'pending',
    pending_item_count: items.length,
    approved_by: null,
    approved_at: null,
    last_change_by: null,
    last_changed_at: null,
    pending_fingerprint: `fake-${tenantId}`,
    items_returned: items.length,
    items_truncated: false,
    items_total: items.length,
    items,
  };
}

export function fakeGetApprovalQueue(tenantId: string): unknown {
  return {
    approval_required: true,
    can_approve: true,
    lessons_pending: 1,
    changes_pending: 2,
    lessons: [fakeApprovalLesson(tenantId)],
  };
}

export function fakeGetApprovalLessonDetail(tenantId: string, lessonId: string): unknown {
  if (lessonId === 'fake-unit-1') return fakeApprovalLesson(tenantId);
  return {
    ...fakeApprovalLesson(tenantId),
    lesson_id: lessonId,
    state: 'approved',
    pending_item_count: 0,
    items_returned: 0,
    items_total: 0,
    items: [],
  };
}

// ---------------------------------------------------------------------------
// feat-321-display-shift-leads — GET /api/tenant/{id}/locations/{lid}/shift-leads.
// Same ManagerResponse shape as …/managers. READ ONLY: the Admin "Deactivate"
// DELETE /api/managers/{id} is deliberately not faked (fake mode never
// acknowledges a destructive write it doesn't persist).
//
// Note: fake mode does not serve `/api/tenant/{id}/locations` (or the location's
// servers/managers), so the location pane is not reachable under start:fake
// today; this branch keeps the shift-leads read from being the one that hits a
// dead network if that ever changes.
// ---------------------------------------------------------------------------
export function fakeGetShiftLeads(tenantId: string, locationId: string): unknown[] {
  return [
    {
      id: `fake-shift-lead-1-${locationId}`,
      email: 'dana.okafor@example.com',
      first_name: 'Dana',
      last_name: 'Okafor',
      phone: null,
      role: 'shift_lead',
      tenant_id: tenantId,
      location_id: locationId,
      created: '2026-08-04T17:12:00',
    },
    {
      id: `fake-shift-lead-2-${locationId}`,
      email: 'luis.ferreira-santos@example.com',
      first_name: 'Luis',
      last_name: 'Ferreira',
      phone: null,
      role: 'shift_lead',
      tenant_id: tenantId,
      location_id: locationId,
      created: '2026-09-21T09:40:00',
    },
  ];
}

/**
 * feat-325 — fake `POST /api/admin/question-items/{id}/test-grade` (CONTRACT §3)
 * so the curriculum editor's "Try an answer" box shows every result kind with no
 * backend. A toy rule, NOT the grading logic: "food"/"comida" (or the variant
 * "real food") is an exact match; an answer containing food/comida is correct;
 * the literal answer "timeout" returns the fallback shape; anything else is
 * incorrect. The mode sent is echoed (null ⇒ lenient, i.e. Auto with no quotes).
 */
export function fakeTestGradeBlank(body: {
  answer?: string;
  locale?: string;
  blank_grading_mode?: 'strict' | 'lenient' | null;
}): unknown {
  const answer = (body.answer ?? '').trim().toLowerCase();
  const mode = body.blank_grading_mode ?? 'lenient';
  if (answer === 'timeout') return { status: 'fallback', error: 'timeout' };
  if (['food', 'la comida', 'comida', 'real food'].includes(answer)) {
    return {
      status: 'graded',
      correct: true,
      reason: 'Exact match',
      mode_applied: mode,
      deterministic_match: true,
      latency_ms: 2,
    };
  }
  const onTopic = /food|comida/.test(answer);
  const correct = mode === 'strict' ? false : onTopic;
  return {
    status: 'graded',
    correct,
    reason: correct
      ? 'A reasonable, on-topic way to finish the sentence.'
      : mode === 'strict'
        ? 'Not the expected wording.'
        : 'Off-topic for this blank.',
    mode_applied: mode,
    deterministic_match: false,
    latency_ms: 640,
  };
}
